import Axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { api_url } from "../../../config";
import Bouton from "../../common/HorsConnexion/Bouton";
import Compte from "../../common/HorsConnexion/Compte";
import EcranResultat from "../../common/HorsConnexion/EcranResultat";
import {
  MESSAGE_SERVEUR_INDISPONIBLE,
  extraireErreurMotDePasse,
  interpreterErreurLien,
} from "../../common/HorsConnexion/horsConnexion";
import MessageEtat from "../../common/HorsConnexion/MessageEtat";
import PageHorsConnexion from "../../common/HorsConnexion/PageHorsConnexion";
import PiedCarte from "../../common/HorsConnexion/PiedCarte";
import ReglesMotDePasse, { evaluerMotDePasse } from "../../common/HorsConnexion/ReglesMotDePasse";
import Saisie from "../../common/HorsConnexion/Saisie";

/**
 * Activation du compte depuis le lien reçu par e-mail (maquettes 06 et 07,
 * évolution du 20/10/2026) : le lien est vérifié dès l'ouverture
 * (GET auth/activation/token/:token, qui ne le consomme pas), puis
 * l'utilisateur choisit son mot de passe et active son compte. Un compte qui a
 * déjà reçu un mot de passe (comptes Caf créés avec un mot de passe initial)
 * peut s'activer sans en choisir un nouveau ; l'API l'exige pour un compte né
 * sans mot de passe, et le dit sous le champ. Un mot de passe refusé laisse
 * le lien utilisable.
 */
const ActivateAccount = (props) => {
  const { token, email } = props.match.params;
  const [vue, setVue] = useState(token && email ? "chargement" : "invalide");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [erreurMdp, setErreurMdp] = useState(null);
  const [erreurGenerale, setErreurGenerale] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);

  const verifierLien = useCallback(() => {
    let demonte = false;
    setVue("chargement");
    Axios.get(api_url + "auth/activation/token/" + encodeURIComponent(token))
      .then(() => {
        if (!demonte) setVue("formulaire");
      })
      .catch((err) => {
        if (!demonte) setVue(interpreterErreurLien(err));
      });
    return () => {
      demonte = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token || !email) return undefined;
    return verifierLien();
  }, [token, email, verifierLien]);

  const { conforme } = evaluerMotDePasse(password);
  const identiques = confirm.length > 0 && confirm === password;
  const peutActiver = conforme && identiques && !enregistrement && !erreurMdp;

  function activer(avecMotDePasse) {
    setEnregistrement(true);
    setErreurGenerale(null);
    const corps = avecMotDePasse ? { token, email, password } : { token, email };
    Axios.post(api_url + "managers/activate-account", corps)
      .then(() => setVue("succes"))
      .catch((err) => {
        const donnees = err.response && err.response.data;
        const messageMdp = extraireErreurMotDePasse(donnees);
        if (messageMdp) {
          setErreurMdp(messageMdp);
          return;
        }
        const resultat = interpreterErreurLien(err);
        if (resultat === "indisponible") setErreurGenerale(MESSAGE_SERVEUR_INDISPONIBLE);
        else setVue(resultat);
      })
      .then(() => setEnregistrement(false));
  }

  function soumettre(e) {
    e.preventDefault();
    if (!peutActiver) return;
    activer(true);
  }

  const versConnexion = { pathname: "/", state: { email } };

  return (
    <PageHorsConnexion>
      {vue === "chargement" && (
        <EcranResultat ton="attente" titre="Activation de votre compte…">
          <p>Un instant, nous vérifions votre lien.</p>
          <Compte email={email} />
        </EcranResultat>
      )}
      {vue === "formulaire" && (
        <section aria-labelledby="titre-activation">
          <h2 id="titre-activation">Activez votre compte</h2>
          <p className="sous-titre">
            Choisissez le mot de passe du compte ci-dessous : vous vous connecterez avec votre adresse e-mail
            professionnelle et ce mot de passe.
          </p>
          <Compte email={email} />

          <form onSubmit={soumettre} noValidate>
            <Saisie
              id="mdp"
              label="Mot de passe"
              revelable
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErreurMdp(null);
              }}
              erreur={erreurMdp}
              aide={<ReglesMotDePasse motDePasse={password} />}
              autoComplete="new-password"
              placeholder="9 caractères minimum"
              autoFocus
            />
            <Saisie
              id="confirm"
              label="Confirmez le mot de passe"
              revelable
              libelleRevelation="la confirmation"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              erreur={confirm.length > 0 && !identiques ? "Les deux mots de passe ne sont pas identiques." : null}
              ok={identiques && conforme && !erreurMdp ? "Les deux mots de passe correspondent." : null}
              autoComplete="new-password"
              placeholder="Saisissez-le une seconde fois"
            />

            {erreurGenerale && (
              <MessageEtat ton="danger" icone="alerte">
                {erreurGenerale}
              </MessageEtat>
            )}

            <Bouton type="submit" charge={enregistrement} disabled={!peutActiver}>
              {enregistrement ? "Activation…" : "Activer mon compte"}
            </Bouton>
            <Bouton
              secondaire
              disabled={enregistrement}
              onClick={() => {
                setErreurMdp(null);
                activer(false);
              }}
            >
              J'ai déjà un mot de passe : activer sans en choisir
            </Bouton>
          </form>

          <PiedCarte signature={false}>Ce lien est valable 3 jours et ne sert qu'une fois.</PiedCarte>
        </section>
      )}
      {vue === "succes" && (
        <EcranResultat ton="succes" icone="coche" titre="Votre compte est activé">
          <p>
            Bienvenue sur ESPOAR. Connectez-vous dès maintenant avec votre adresse e-mail professionnelle et le mot
            de passe que vous venez de choisir.
          </p>
          <Compte email={email} />
          <Bouton to={versConnexion}>Se connecter</Bouton>
        </EcranResultat>
      )}
      {vue === "expire" && (
        <EcranResultat ton="avert" icone="horloge" titre="Ce lien a expiré">
          <p>
            Les liens d'activation sont valables <strong>3 jours</strong> après leur envoi. Celui-ci
            n'est plus utilisable.
          </p>
          <div className="note">
            Rapprochez-vous de la personne qui a créé votre compte : elle peut vous renvoyer un
            nouveau lien d'activation depuis votre fiche.
          </div>
          <Bouton secondaire to="/">
            Retour à la connexion
          </Bouton>
        </EcranResultat>
      )}
      {vue === "utilise" && (
        <EcranResultat ton="info" icone="info" titre="Ce lien a déjà été utilisé">
          <p>Un lien d'activation ne sert qu'une fois. Votre compte est très probablement déjà actif.</p>
          <Compte email={email} />
          <Bouton to={versConnexion}>Se connecter</Bouton>
        </EcranResultat>
      )}
      {vue === "invalide" && (
        <EcranResultat ton="danger" icone="croix" titre="Ce lien est invalide">
          <p>
            Il est peut-être tronqué ou mal recopié. Ouvrez-le directement depuis l'e-mail que vous
            avez reçu.
          </p>
          <div className="note">
            Si le problème persiste, demandez un nouveau lien à la personne qui a créé votre compte.
          </div>
          <Bouton secondaire to="/">
            Retour à la connexion
          </Bouton>
        </EcranResultat>
      )}
      {vue === "indisponible" && (
        <EcranResultat ton="danger" icone="alerte" titre="Activation impossible pour le moment">
          <p>
            Le serveur n'a pas répondu. Réessayez dans quelques instants en rouvrant le lien reçu
            par e-mail.
          </p>
          <Bouton onClick={verifierLien}>Réessayer</Bouton>
          <Bouton secondaire to="/">
            Retour à la connexion
          </Bouton>
        </EcranResultat>
      )}
    </PageHorsConnexion>
  );
};

export default ActivateAccount;
